import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type PageHeadingLevel = 'h1' | 'h2'

export type PageHeadingSize = 'default' | 'settings'

export type PageHeadingProps = {
  title?: ReactNode
  description?: ReactNode
  icon?: ReactNode
  iconClassName?: string
  showTitleIcon?: boolean
  level?: PageHeadingLevel
  size?: PageHeadingSize
  className?: string
  titleClassName?: string
  id?: string
  /** Spread onto the title element (`h1` / `h2`). */
  titleProps?: Omit<ComponentPropsWithoutRef<'h1'>, 'children' | 'className' | 'id'>
}

export function hasVisiblePageHeaderDescription(description: ReactNode): boolean {
  if (description == null || description === false) return false
  if (typeof description === 'string') return description.trim().length > 0
  return true
}

export function PageHeading({
  title,
  description,
  icon,
  iconClassName,
  showTitleIcon = false,
  level = 'h1',
  size = 'default',
  className,
  titleClassName,
  id,
  titleProps,
}: PageHeadingProps) {
  const hasTitle = Boolean(title)
  const hasDescription = hasVisiblePageHeaderDescription(description)
  const compact = hasTitle && !hasDescription
  const TitleTag = level === 'h1' ? 'h1' : 'h2'

  const textColumn =
    hasTitle || hasDescription ? (
      <div className={cn('min-w-0 w-full', compact && 'space-y-0')}>
        {hasTitle ? (
          <TitleTag
            id={id}
            {...titleProps}
            className={cn(
              size === 'settings' ? 'text-xl' : 'text-2xl',
              'font-semibold tracking-tight break-words text-foreground',
              compact && (size === 'settings' ? 'leading-tight' : 'leading-none'),
              hasDescription && 'mb-1.5',
              compact && 'mb-0 pb-0',
              titleClassName,
            )}
          >
            {title}
          </TitleTag>
        ) : null}
        {hasDescription ? (
          <p className="min-w-0 break-words text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
    ) : null

  if (!textColumn && !(showTitleIcon && icon)) {
    return null
  }

  if (!showTitleIcon || !icon) {
    return textColumn ? (
      <div className={cn(compact && 'pb-0', className)}>
        {textColumn}
      </div>
    ) : null
  }

  return (
    <div
      className={cn(
        'flex w-full min-w-0 gap-3',
        compact ? 'items-center' : 'items-start',
        compact && 'pb-0',
        className,
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary',
          compact ? 'h-8 w-8' : 'h-10 w-10',
          iconClassName,
        )}
      >
        {icon}
      </div>
      {textColumn}
    </div>
  )
}
